import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { portalNotificationsApi, type PortalNotificationFeedItem } from "@/api/portalNotifications";
import { Button, Skeleton, Timestamp } from "@/design-system/components/ui";
import { usePortalNotifications } from "../components/PortalNotificationsContext";
import { safePortalReturnTo } from "../lib/returnTo";

// External Collection → Notifications — the real portal feed. Newest first;
// unread items are visually distinct (gold dot + bold + tint) and read items
// render normal. Items stay in the feed as history once read.
//
// Step 5 W2 — the feed LOOKED interactive and was not. It rendered an unread
// dot and a bold title, implying a read/unread concept the reader could act
// on, while every list item was a bare <li> with no handler. Opening the tab
// marked EVERYTHING read, so a vendor who read two of five and got
// interrupted came back to a feed with no unread state left and no way to
// find their place.
//
// So: opening the tab no longer marks anything read, each item is a button
// that marks itself read, and one explicit control does the old thing for
// anyone who wants it. `markRead` already accepts both shapes — a list of
// ids, or null for all — so this needed no backend change.
//
// Approval chains (ruling Q14) — a system "CollectionChain" notice (your turn,
// returned, fully signed) carries a `linkUrl` to its item and renders an
// "Open" link beside the row. The link is offered only for a same-origin
// `/portal/` path (`safePortalReturnTo`); anything else renders no link.
// Staff announcements carry no target and are unchanged.

/** The feed row as the portal reads it. Widened locally (this tree owns no
 *  shared `@/api` type): the chain notice type and its link. */
type PortalFeedRow = Omit<PortalNotificationFeedItem, "type"> & {
  type: PortalNotificationFeedItem["type"] | "CollectionChain";
  linkUrl?: string | null;
};

/** The in-portal path a chain notice opens, or null when it has none we trust. */
export function notificationLinkFor(n: Pick<PortalFeedRow, "type" | "linkUrl">): string | null {
  if (n.type !== "CollectionChain") return null;
  return safePortalReturnTo(n.linkUrl ?? null);
}

export function NotificationsTab() {
  const { refresh } = usePortalNotifications();
  const [items, setItems] = useState<PortalFeedRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [markingAll, setMarkingAll] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data: PortalFeedRow[] = await portalNotificationsApi.list();
        if (!cancelled) setItems(data);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load notifications.");
      }
    })();
    return () => {
      cancelled = true;
    };
    // Load once on mount. Opening the tab is NO LONGER the read trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Stamp the given rows read locally so the dot clears without a refetch. */
  const stampRead = useCallback((ids: string[] | null) => {
    const at = new Date().toISOString();
    setItems((prev) =>
      prev === null
        ? prev
        : prev.map((n) =>
            n.readAt == null && (ids === null || ids.includes(n.id)) ? { ...n, readAt: at } : n,
          ),
    );
  }, []);

  const markOne = useCallback(
    async (id: string) => {
      setBusyId(id);
      try {
        await portalNotificationsApi.markRead([id]);
        stampRead([id]);
        // The bell/badge is shared state the tab does not own.
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Couldn't mark that notification read.");
      } finally {
        setBusyId(null);
      }
    },
    [refresh, stampRead],
  );

  const markAll = useCallback(async () => {
    setMarkingAll(true);
    try {
      // null => every currently-unread row for this account, server-scoped.
      await portalNotificationsApi.markRead();
      stampRead(null);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't mark notifications read.");
    } finally {
      setMarkingAll(false);
    }
  }, [refresh, stampRead]);

  if (error) {
    return <p className="text-small text-error">{error}</p>;
  }

  if (items === null) {
    return (
      <div className="flex flex-col gap-space-2" aria-label="Loading notifications" aria-busy="true">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <section
        aria-label="Notifications"
        className="p-space-5 border border-dashed border-border-light rounded-md bg-white-pure text-center"
      >
        <p className="text-small text-gray-mid">No notifications yet.</p>
      </section>
    );
  }

  const unreadCount = items.filter((n) => n.readAt == null).length;

  return (
    <div className="flex flex-col gap-space-3">
      {unreadCount > 0 && (
        <div className="flex items-center justify-between gap-space-3">
          <p className="text-small text-gray-mid m-0">
            {unreadCount} unread
          </p>
          <Button
            variant="text"
            onClick={() => void markAll()}
            disabled={markingAll}
            data-testid="notifications-mark-all"
          >
            {markingAll ? "Marking…" : "Mark all as read"}
          </Button>
        </div>
      )}

      <ul className="flex flex-col gap-space-2" aria-label="Notifications">
        {items.map((n) => {
          const unread = n.readAt == null;
          const link = notificationLinkFor(n);
          return (
            <li key={n.id} className="flex flex-col gap-space-1">
              {/* A read item has nothing left to do, so it stops being a
                  button rather than becoming a no-op one — a control that
                  does nothing is worse than no control. */}
              <ItemShell
                unread={unread}
                busy={busyId === n.id}
                onMarkRead={unread ? () => void markOne(n.id) : undefined}
              >
                <div className="flex items-start gap-space-2">
                  {unread && (
                    <span
                      className="mt-[6px] h-2 w-2 shrink-0 rounded-pill bg-gold"
                      aria-label="Unread"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-space-3 gap-y-space-1">
                      <h3
                        className={
                          "text-small text-onyx truncate " +
                          (unread ? "font-bold" : "font-semibold")
                        }
                      >
                        {n.title}
                      </h3>
                      <Timestamp value={n.createdAt} className="shrink-0" />
                    </div>
                    <p className="text-small text-onyx whitespace-pre-wrap mt-space-1">{n.body}</p>
                  </div>
                </div>
              </ItemShell>
              {/* Beside the row, never inside it: an unread row is itself a
                  button, and a link nested in a button is invalid markup. */}
              {link && (
                <Link
                  to={link}
                  className="self-end text-small font-medium text-onyx underline hover:text-gold-shadow"
                  data-testid={`notification-open-${n.id}`}
                >
                  Open
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Unread rows are a real button (keyboard-reachable, labelled); read rows
 *  are inert markup. Same box either way. */
function ItemShell({
  unread,
  busy,
  onMarkRead,
  children,
}: {
  unread: boolean;
  busy: boolean;
  onMarkRead?: () => void;
  children: React.ReactNode;
}) {
  const box =
    "w-full rounded-md border px-space-4 py-space-3 " +
    (unread ? "border-gold/40 bg-gold/5" : "border-border-light bg-white-pure");

  if (!onMarkRead) {
    return <div className={box}>{children}</div>;
  }
  return (
    <button
      type="button"
      onClick={onMarkRead}
      disabled={busy}
      aria-label="Mark as read"
      className={box + " text-left hover:border-gold focus-visible:border-gold disabled:opacity-60"}
    >
      {children}
    </button>
  );
}
